import type { ICustomerService } from '../interface/ICustomerService';
import type { Customer, CustomerListResponse, UpdateCustomerRequest, GetUsersLinkedToCustomerResponse } from '../../models/customerModel';
import type { IHttpWrapperService } from '../interface/IHttpWrapperService';
import { container, TYPES } from '../inversify.config';
import type { AxiosResponse } from 'axios';
import type { ApiResponse } from '../../models/ApiResponse';
import { isSuccessResponse } from '../../models/ApiResponse';
import { injectable } from 'inversify';
import 'reflect-metadata';


export class CustomerService implements ICustomerService {
    private httpService: IHttpWrapperService = container.get(TYPES.IHttpWrapperService);

    async GetCustomerById(id: string): Promise<Customer | null> {
        try {
            const response: AxiosResponse<ApiResponse<Customer>> = await this.httpService.getRequest<Customer>(`customer/domain/getcustomerdetails?id=${id}`);
            
            if (isSuccessResponse(response.data)) {
                return response.data.data;
            } else {
                return null;
            }
        } catch (error) {
            return null;
        }
    }

    async GetAllCustomers(pageNumber: number = 1, pageSize: number = 10): Promise<CustomerListResponse> {
        try {
            const endpoint = `customer/domain/getallcustomers?pageNumber=${pageNumber}&pageSize=${pageSize}`;
            const response: AxiosResponse<ApiResponse<CustomerListResponse>> = await this.httpService.getRequest<CustomerListResponse>(endpoint);
            
            if (!response) {
                throw new Error('No response received from server');
            }
            
            if (isSuccessResponse(response.data)) {
                return response.data.data;
            } else {
                throw new Error(response.data.message || 'Failed to fetch customers');
            }
        } catch (error) {
            throw error;
        }
    }

    async GetAllCustomersBoardView(): Promise<CustomerListResponse> {
        try {
            const endpoint = `customer/domain/getallcustomersboardview`;
            const response: AxiosResponse<ApiResponse<CustomerListResponse>> = await this.httpService.getRequest<CustomerListResponse>(endpoint);
            
            if (!response) {
                throw new Error('No response received from server');
            }
            
            if (isSuccessResponse(response.data)) {
                return response.data.data;
            } else {
                throw new Error(response.data.message || 'Failed to fetch customers for board view');
            }
        } catch (error) {
            throw error;
        }
    }

    async CreateCustomer(customer: Omit<Customer, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>): Promise<Customer> {
        try {
            const response: AxiosResponse<ApiResponse<Customer>> = await this.httpService.postRequest<Customer, typeof customer>('customer/domain/createcustomer', customer);
            
            if (isSuccessResponse(response.data)) {
                return response.data.data;
            } else {
                throw new Error(response.data.message || 'Failed to create customer');
            }
        } catch (error) {
            throw error;
        }
    }

    async UpdateCustomer(id: string, customerData: UpdateCustomerRequest): Promise<void> {
        try {
            const endpoint = `customer/domain/updatecustomer/${id}`;
            const response: AxiosResponse<ApiResponse<void>> = await this.httpService.putRequest<void, UpdateCustomerRequest>(endpoint, customerData);
            
            if (!isSuccessResponse(response.data)) {
                throw new Error(response.data.message || 'Failed to update customer');
            }
        } catch (error) {
            throw error;
        }
    }

    async SubmitForDevOpsApproval(id: string): Promise<void> {
        try {
            const endpoint = `customer/domain/submitfordevopsapproval?id=${id}`;
            const response: AxiosResponse<ApiResponse<void>> = await this.httpService.postRequest<void, undefined>(endpoint, undefined);
            
            if (!isSuccessResponse(response.data)) {
                throw new Error(response.data.message || 'Failed to submit for DevOps approval');
            }
        } catch (error) {
            throw error;
        }
    }

    async ApproveDomain(customerId: string): Promise<void> {
        try {
            const endpoint = `domain/approvedomain?customerid=${customerId}`;
            const response: AxiosResponse<ApiResponse<void>> = await this.httpService.postRequest<void, undefined>(endpoint, undefined);
            
            if (!isSuccessResponse(response.data)) {
                throw new Error(response.data.message || 'Failed to approve domain');
            }
        } catch (error) {
            throw error;
        }
    }

    async RejectDomain(customerId: string): Promise<void> {
        try {
            const endpoint = `domain/rejectdomain?customerid=${customerId}`;
            const response: AxiosResponse<ApiResponse<void>> = await this.httpService.postRequest<void, undefined>(endpoint, undefined);
            
            if (!isSuccessResponse(response.data)) {
                throw new Error(response.data.message || 'Failed to reject domain');
            }
        } catch (error) {
            throw error;
        }
    }

    async GetUsersLinkedToCustomer(customerId: string): Promise<GetUsersLinkedToCustomerResponse> {
        try {
            const endpoint = `customer/domain/getuserslinkedtocustomer/${customerId}`;
            const response: AxiosResponse<ApiResponse<GetUsersLinkedToCustomerResponse>> = await this.httpService.getRequest<GetUsersLinkedToCustomerResponse>(endpoint);
            
            if (isSuccessResponse(response.data)) {
                return response.data.data;
            } else {
                throw new Error(response.data.message || 'Failed to fetch users linked to customer');
            }
        } catch (error) {
            throw error;
        }
    }

    async LinkUsersToCustomer(customerId: string, userIds: string[]): Promise<void> {
        try {
            const endpoint = 'customer/domain/linkuserstocustomer';
            const requestBody = {
                customerId,
                userIds
            };
            const response: AxiosResponse<ApiResponse<void>> = await this.httpService.postRequest<void, typeof requestBody>(endpoint, requestBody);
            
            if (!isSuccessResponse(response.data)) {
                throw new Error(response.data.message || 'Failed to link users to customer');
            }
        } catch (error) {
            throw error;
        }
    }

    async ValidateDataEntryStage(partnerCode: string): Promise<{
        isSuccess: boolean;
        data: {
            isValid: boolean;
            entities: string[];
        };
        message: string;
    }> {
        try {
            const endpoint = `customer/domain/validatedataentrystage?partnerCode=${partnerCode}`;
            const response: AxiosResponse<ApiResponse<{ isValid: boolean; entities: string[] }>> = 
                await this.httpService.getRequest<{ isValid: boolean; entities: string[] }>(endpoint);
            
            return {
                isSuccess: response.data.isSuccess,
                data: response.data.data,
                message: response.data.message || ''
            };
        } catch (error) {
            throw error;
        }
    }

    async UpdateOnboardingStatus(customerId: string, status: string): Promise<{
        isSuccess: boolean;
        data: any;
        message: string;
    }> {
        try {
            const endpoint = `customer/domain/onboardingstatusupdate/${customerId}?onboardingStatus=${status}`;
            const response: AxiosResponse<ApiResponse<any>> = 
                await this.httpService.putRequest<any, undefined>(endpoint, undefined);
            
            return {
                isSuccess: response.data.isSuccess,
                data: response.data.data,
                message: response.data.message || ''
            };
        } catch (error) {
            throw error;
        }
    }
}
